import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';

import { cn } from './cn';

/**
 * The small set of primitives the Sprint-1 shell needs.
 *
 * Every surface is square-cornered: `tailwind.config.ts` zeroes ALL radii, and
 * controls opt back in with an explicit `rounded-[8px]` per the token doc.
 */

type ButtonVariant = 'primary' | 'ghost' | 'outline' | 'danger';

const BUTTON_VARIANTS: Readonly<Record<ButtonVariant, string>> = Object.freeze({
  // The design's primary control: 90° brand gradient, bold, no border.
  primary: 'bg-brand-gradient font-bold text-white hover:brightness-110 active:brightness-95',
  ghost: 'font-semibold text-neutral-700 hover:bg-neutral-100 hover:text-ink',
  outline:
    'border border-neutral-300 bg-surface-raised font-semibold text-ink hover:border-ink',
  /** For no-undo destructive actions only (e.g. "Delete booking") — never Cancel/Revise. */
  danger: 'bg-danger font-bold text-white hover:brightness-110 active:brightness-95',
});

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  icon?: ReactNode;
}

export function Button({
  variant = 'primary',
  icon,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps): JSX.Element {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-[7px] rounded-[8px] px-[15px] py-[11px]',
        'text-[13px] leading-none transition-[filter,background-color,border-color] duration-150',
        'disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:border-neutral-300 disabled:hover:brightness-100',
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

export function Card({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      className={cn('border border-neutral-200 bg-surface-raised p-5 shadow-sm', className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }): JSX.Element {
  return <div className={cn('animate-pulse bg-neutral-200', className)} aria-hidden="true" />;
}

/** Full-region loading state used by the guards while `/auth/me` is in flight. */
export function PageSkeleton(): JSX.Element {
  return (
    <div className="space-y-4 p-6" role="status" aria-label="Loading">
      <Skeleton className="h-7 w-56" />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-28" />
        ))}
      </div>
      <Skeleton className="h-64" />
    </div>
  );
}

/** Centred state for boot, before the shell itself is allowed to render. */
export function FullPageSpinner({ label = 'Loading' }: { label?: string }): JSX.Element {
  return (
    <div className="flex h-full w-full items-center justify-center bg-canvas" role="status" aria-label={label}>
      {/* `rounded-full` is what makes this a spinner rather than a tumbling
          square — without it the border box just rotates on its corner. */}
      <div className="h-9 w-9 animate-spin rounded-full border-2 border-neutral-300 border-t-brand" />
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
      <h1 className="text-2xl tracking-tight text-ink">{title}</h1>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-neutral-600">{description}</p>
      {action !== undefined && <div className="mt-6">{action}</div>}
    </div>
  );
}
